import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, concat, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, takeLast, tap } from 'rxjs/operators';
import { amountAs, makeAmountWith, Translator, TRANSLATOR, UOMS } from '@miq/utils';
import { CatalogMethod, CatalogsService, LeaseColumns } from '@miq/catalog';
import { EfficiencyTransport } from '../../models';
import { NonRentableSpaceClass } from '../../models/space/non-rentable-space.class';
import { SHOW_FLOOR_INDEX_FEATURE_KEY } from '../../shared/constants/features.const';
import { NotificationService } from '../../shared/services/notification.service';
import { findOwnerOccupiedColumn, isTenantEntityActive } from '../../shared/utils/functions';
import { StackingPlanService } from '../../stacking-plan.service';
import { MoreInfoDialogComponent } from '../dialogs/more-info-dialog/more-info-dialog.component';
import { NonRentableSpaceComponent } from '../dialogs/non-rentable-space/non-rentable-space.component';
import { NonRentableData } from '../dialogs/non-rentable-space/non-rentable-space.model';
import { PendingIssuesComponent } from '../dialogs/pending-issues/pending-issues.component';
import { categorizeIssues } from '../dialogs/pending-issues/pending-issues.functions';
import { UpdateStackingPlanInfo } from '../floor-container/floor/floor.model';
import { SuiteType } from '../floor-container/floor/item/item.model';
import { StackingPlanContainer } from '../stacking-plan.model';
import { ReqPropertyInfo, Toolbar } from './actions-toolbar.model';
import { ActionsToolbarService } from './actions-toolbar.service';
import { SettingComponent } from './settings/settings.component';
import { Settings } from './settings/settings.model';
import { SettingsService } from './settings/settings.service';
import { IdentifiedIssue, IssueField } from '../dialogs/pending-issues/pending-issues.model';
import { FloorContainerRepository } from '../floor-container/floor-container-repository.service';
import { ElevatorBanksRepository } from '../floor-container/elevator-banks/elevator-banks-repository.service';
import { EXPORT_RESOLVER_TOKEN } from './export/export-resolvers/export-resolver.model';
import { SPExportResolver } from './export/export-resolvers/sp-export-resolver';
import { UserRole, hasOneOfTheRoles } from '@miq/user';
import { SPDataStatus } from '../../stacking-plan.model';
import { StackPlanViewStateService } from '../../state/sp-view-state.service';
import { LocalizationOptionsService } from '../../localization-option.service';
import { FloorDataChangesHelperService } from './helpers/floor-data-changes.helper';
import { ElevatorChangesHelperService } from './helpers/elevator-changes.helper';

@UntilDestroy()
@Component({
  selector: 'sp-actions-toolbar',
  templateUrl: './actions-toolbar.component.html',
  styleUrls: ['./actions-toolbar.component.scss'],
  providers: [{ provide: EXPORT_RESOLVER_TOKEN, useClass: SPExportResolver }],
  standalone: false,
})
export class ActionsToolbarComponent implements OnInit, OnDestroy {
  @Output() createNewRecord: EventEmitter<EfficiencyTransport> = new EventEmitter();
  hasEditionPerms = signal(false);
  hideActionButtons = signal(false);

  /** @deprecated temporary usage */
  showFloorIndexFeatureKey = SHOW_FLOOR_INDEX_FEATURE_KEY;
  private _toolbarData: Toolbar;
  private pendingIssues: IdentifiedIssue[] = [];
  // private floorsElevatorsInfo: FloorsElevators;
  fetchPendingIssues$ = new BehaviorSubject(null);
  pendingIssues$ = this.getPendingIssues();
  isOwnerOccupiedActiveInCatalog$ = this.isOwnerOccupiedInCatalog();
  isTenantActive = isTenantEntityActive();

  totalNumberOfFloors = 0;
  totalVacancySpace = 0;
  totalVacancySpaceUom = 'Square Feet';
  buildingSpaceValue = 0;
  buildingSpaceUOMName = 'Square Feet';
  buildingVacancyPercent = 0;
  public isTrustedUser = false;
  public hasBrokerRole = false;
  constructor(
    private notificationService: NotificationService,
    private stackingPlanService: StackingPlanService,
    private spStateService: StackPlanViewStateService,
    private actionsToolbarService: ActionsToolbarService,
    private settingsService: SettingsService,
    @Inject(TRANSLATOR) private translationService: Translator,
    private catalogService: CatalogsService,
    private floorsRepository: FloorContainerRepository,
    private elevatorBanksRepository: ElevatorBanksRepository,
    private localizationOptionsService: LocalizationOptionsService,
    private floorDataChangesHelper: FloorDataChangesHelperService,
    private elevatorChangesHelper: ElevatorChangesHelperService
  ) {}

  #toggleButtonEnable = (containerData: StackingPlanContainer) => {
    this.hideActionButtons.set(containerData?.dataStatus === SPDataStatus.ServerError);
  };

  #hasEditionPerms = () => {
    const user = this.stackingPlanService.resolveUser();
    this.hasBrokerRole = hasOneOfTheRoles(user, [UserRole.USBroker, UserRole.InvestorBroker, UserRole.OccupierBroker]);
    return (
      this.stackingPlanService.isTrustedUser() &&
      hasOneOfTheRoles(user, [UserRole.USResearcher, UserRole.DandT, UserRole.DIGUser])
    );
  };

  ngOnInit() {
    this.hasEditionPerms.set(this.#hasEditionPerms());
    this.getSettings();
    this.stackingPlanService.containerData$
      .pipe(untilDestroyed(this), tap(this.#toggleButtonEnable))
      .subscribe(() => this._setToolbarData(this.stackingPlanService.toolbarData));
  }

  private _setToolbarData = (value: Toolbar) => {
    if (!value) {
      return;
    }
    this._toolbarData = value;
    this.totalNumberOfFloors = value.totalNumberOfFloors;
    this.identifyBuildingUnitsAndUOM();
    this.fetchPendingIssues$.next(null);
  };

  ngOnDestroy() {
    /* required by untilDestroyed */
  }

  onPendingIssues() {
    this.notificationService.openDialog(PendingIssuesComponent, this.pendingIssues, {
      panelClass: 'table-modals',
      autoFocus: false,
    });
  }

  /**
   * Open the more info dialog box and sends the floors and elevators data to
   * it. When dialog box close in more info component it will receives the
   * floor/elevator the latest data passed to the custom service
   * {@link StackingPlanService.setUpdatedStackingData} and updates the behavior
   * object.
   */
  /* more-info dialog */
  onMoreInfo(): void {
    const floorsElevatorsInfo = {
      floorInfo: this.floorsRepository.floorData,
      processedElevatorInfo: this.elevatorBanksRepository.elevatorBanksMatrix,
      elevatorInfo: this.elevatorBanksRepository.elevatorBanks,
    };
    const localeId = this.localizationOptionsService.getInstance().localeId;
    this.notificationService
      .openDialog<UpdateStackingPlanInfo>(
        MoreInfoDialogComponent,
        { floorsHeaderData: this._toolbarData, floorsElevatorsInfo, localeId },
        { panelClass: ['table-modals', 'sp-manage-floors-dialog'] }
      )
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe((modifiedSPInfo: UpdateStackingPlanInfo) => {
        const { updatedFloorData, elevatorBank, deletedElevators } = modifiedSPInfo;
        const { floorInfo, elevatorInfo } = floorsElevatorsInfo;
        const requestsToBeMade: Array<Observable<any>> = [];

        if (modifiedSPInfo.toolbarData != null && modifiedSPInfo.headerChanged !== false) {
          requestsToBeMade.push(this.updateUndergroundFloors(modifiedSPInfo.toolbarData));
        }

        // Get the changes to be made for the updated Floor Data (changes related to floors)
        requestsToBeMade.push(...this.floorDataChangesHelper.getUpdatedFloorData(updatedFloorData, floorInfo));

        // Requests made for elevator needs to follow the order of delete and then create
        const elevatorRequests = this.elevatorChangesHelper.getElevatorRequests(
          elevatorBank,
          deletedElevators,
          elevatorInfo
        );

        if (elevatorRequests.length > 0) {
          requestsToBeMade.push(concat(...elevatorRequests));
        }

        // one refresh per save, after every write (serial order kept for MIQ-26431)
        concat(...requestsToBeMade)
          .pipe(takeLast(1))
          .subscribe(() => {
            /*
             * If any changes are made for toolbar data, floor data or elevator data
             * setUpdatedStackingData is called so the data in stacking plan
             * container gets updated
             */
            this.stackingPlanService.setUpdatedStackingData({ toolbarData: modifiedSPInfo.toolbarData });
            this.refreshSPData();
          });
      });
  }

  refreshSPData(): void {
    this.stackingPlanService.setSpaceChanges();
  }

  /**
   * Get current user settings and open the settings dialog. On close and if
   * the user saved something, it requests to store the settings
   */
  onSettings(): void {
    this.notificationService
      .openDialog(SettingComponent, this.settingsService.getSettings(), { panelClass: 'alert-modals' })
      .pipe(filter(Boolean))
      .subscribe((settingsData: Settings) =>
        this.stackingPlanService
          .saveSettings(settingsData)
          .subscribe(() => this.settingsService.setSettings(settingsData))
      );
  }

  onNewNonRentableSpace(): void {
    const data: NonRentableData = {
      item: null,
      floorNo: null,
      totalNumberOfFloors: this.totalNumberOfFloors,
    };
    this.notificationService
      .openDialog<{ floorNumber: string; nonRentableSpace: NonRentableSpaceClass }>(NonRentableSpaceComponent, data, {
        panelClass: 'table-modals',
        autoFocus: false,
      })
      .pipe(filter(nonRentableSpace => nonRentableSpace != null))
      .subscribe(() => this.refreshSPData());
  }

  private identifyBuildingUnitsAndUOM(): void {
    const { netRentableArea, netRentableAreaUOM, totalVacancyArea, totalVacancyAreaUOM } = this._toolbarData;
    this.buildingSpaceValue = netRentableArea;
    this.buildingSpaceUOMName = netRentableAreaUOM;

    this.totalVacancySpace = totalVacancyArea;
    this.totalVacancySpaceUom = totalVacancyAreaUOM;

    const totalVacancySpaceAmountSF = amountAs(
      UOMS.SQUARE_FOOT.unit,
      makeAmountWith({ value: this.totalVacancySpace, uom: totalVacancyAreaUOM ?? UOMS.SQUARE_FOOT.type })
    );

    const buildingSpaceValueSF = amountAs(
      UOMS.SQUARE_FOOT.unit,
      makeAmountWith({ value: this.buildingSpaceValue, uom: this.buildingSpaceUOMName ?? UOMS.SQUARE_FOOT.type })
    );

    this.buildingVacancyPercent =
      !totalVacancySpaceAmountSF || buildingSpaceValueSF == null ? 0 : totalVacancySpaceAmountSF / buildingSpaceValueSF;
  }

  private getPendingIssues(): Observable<{ expired: number; unknown: number }> {
    return this.fetchPendingIssues$
      .pipe(switchMap(() => this.stackingPlanService.getPendingIssuesData(this.spStateService.getState())))
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        switchMap(pendingIssues => {
          this.pendingIssues = pendingIssues.map(issue => {
            issue.name = this.issueTranslate(issue.name);
            issue.recordType = this.issueTranslate(issue.recordType) as SuiteType;
            const issueFields: { [key: string]: IssueField } = this.getIssueFields(issue);
            return { ...issue, ...issueFields };
          });
          this.stackingPlanService.pendingIssues = pendingIssues;
          const { expired, unknown } = categorizeIssues(pendingIssues);
          return of({ expired: expired.length, unknown: unknown.length });
        }),
        catchError(() => of({ expired: 0, unknown: 0 }))
      );
  }

  private isOwnerOccupiedInCatalog(): Observable<boolean> {
    return this.catalogService
      ?.getColumns(SuiteType.Lease, CatalogMethod.Default)
      .pipe(map(sections => findOwnerOccupiedColumn(sections, LeaseColumns.LeaseCompDetails_OwnerOccupied)));
  }

  private issueTranslate(value: string): string {
    return value.includes('filters.') ? this.translationService.translate(value) : value;
  }

  private getIssueFields(issue: IdentifiedIssue): { [key: string]: IssueField } {
    return Object.entries(issue).reduce((acc, [key, issueField]) => {
      let result = acc;
      if (typeof issueField === 'object') {
        let value = issueField.value;
        if (value && typeof value === 'string') {
          value = value.includes('filters.') ? this.translationService.translate(value) : value;
          result = { ...acc, [key]: { ...issueField, value } };
        }
        return result;
      }
    }, {});
  }

  private get propertyId() {
    return this.spStateService.getState().id;
  }

  private updateUndergroundFloors(toolbarChanges: Toolbar): Observable<any> {
    const {
      underGroundFloors,
      totalNumberOfFloors,
      netRentableArea,
      netRentableAreaUOM,
      commonFloorSize,
      commonFloorSizeUOM,
    } = toolbarChanges;
    const reqUndergroundFloors = new ReqPropertyInfo(
      this.propertyId,
      underGroundFloors,
      totalNumberOfFloors,
      netRentableArea,
      netRentableAreaUOM,
      commonFloorSize,
      commonFloorSizeUOM
    );
    return this.actionsToolbarService
      .updateUndergroundFloors(reqUndergroundFloors)
      .pipe(this.actionsToolbarService.errorHandling('errorMessages.updateUndergroundFloor'));
  }

  /** Request to get current user settings */
  private getSettings() {
    this.stackingPlanService
      .getSettings()
      .pipe(filter(Boolean))
      .subscribe(settings => this.settingsService.setSettings(this.getDefaultSettings(settings)));
  }

  private getDefaultSettings(settings: Settings) {
    return {
      ...settings,
      optimizeScale: {
        isOptimzeScale: true,
        minimunScale: 1,
      },
    };
  }
}
